import { queryOptions, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { notificationsApi, type Notification } from '@/shared/api/notifications';
import { portalTarget, type PortalTarget } from '@/shared/navigation/manifest';
import { sessionQueryOptions } from '@/shared/session/session';

/**
 * Query options and the pure rules of the notification centre (13 §6.2): kept apart from the bell
 * and the page so the entity-routing table and the body truncation are each tested on their own.
 */

/** The bell's own unread count is the same collection, at its narrowest (13 §6.2). */
export function notificationsUnreadCountQueryOptions() {
  return queryOptions({
    queryKey: ['notifications', 'unreadCount'] as const,
    queryFn: ({ signal }) =>
      notificationsApi.list(
        { limit: 1, countExact: true, filters: { 'readAt[isNull]': 'true' } },
        signal,
      ),
    // 13 §6.2: low-frequency polling, never a persistent connection.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** The 20 most recent, newest first, for the bell's open state. */
export function notificationsRecentQueryOptions() {
  return queryOptions({
    queryKey: ['notifications', 'recent'] as const,
    queryFn: ({ signal }) => notificationsApi.list({ limit: 20, sort: '-createdAt' }, signal),
  });
}

export function notificationPreferenceCatalogQueryOptions() {
  return queryOptions({
    queryKey: ['notifications', 'preferenceCatalog'] as const,
    queryFn: ({ signal }) => notificationsApi.preferenceCatalog(signal),
    staleTime: 60_000,
  });
}

export function notificationPreferencesQueryOptions() {
  return queryOptions({
    queryKey: ['notifications', 'preferences'] as const,
    queryFn: ({ signal }) => notificationsApi.preferences(signal),
  });
}

/** First 120 characters of the body, for the bell's compact list (13 §6.2). */
export function truncateBody(body: string, maxLength = 120): string {
  return body.length <= maxLength ? body : `${body.slice(0, maxLength)}…`;
}

/** The cap the unread badge never reads past (13 §6.2). */
export const UNREAD_COUNT_CEILING = 99;

/** The badge text, or `null` when there is nothing unread to show. */
export function unreadBadge(
  page: { totalCount: number | null; countExceededLimit: boolean } | undefined,
): string | null {
  if (page === undefined) {
    return null;
  }
  if (page.countExceededLimit) {
    return `${String(UNREAD_COUNT_CEILING)}+`;
  }
  if (page.totalCount === null || page.totalCount <= 0) {
    return null;
  }
  return page.totalCount > UNREAD_COUNT_CEILING
    ? `${String(UNREAD_COUNT_CEILING)}+`
    : String(page.totalCount);
}

/** Where one notification leads (13 §6.2): the routes this console actually has, `/notifications` for the rest. */
export type NotificationTarget =
  | { kind: 'ticket'; ticketId: string }
  | { kind: 'invoice'; invoiceId: string }
  | { kind: 'billingPeriod'; periodId: string }
  | { kind: 'contract'; contractId: string }
  | { kind: 'knowledgeArticle'; articleId: string }
  | { kind: 'supportAccess' }
  | { kind: 'none' };

export function notificationTarget(
  notification: Pick<Notification, 'entityType' | 'entityId'>,
): NotificationTarget {
  if (notification.entityId === null) {
    return { kind: 'none' };
  }

  switch (notification.entityType) {
    case 'ticket':
      return { kind: 'ticket', ticketId: notification.entityId };
    case 'invoice':
      return { kind: 'invoice', invoiceId: notification.entityId };
    case 'billing_period':
      return { kind: 'billingPeriod', periodId: notification.entityId };
    case 'contract':
      return { kind: 'contract', contractId: notification.entityId };
    // The notices of an article in review or whose review went by lead to the article itself.
    case 'knowledge_article':
      return { kind: 'knowledgeArticle', articleId: notification.entityId };
    // The notices of a support access name the grant (migration 0063); the grants have no page of
    // their own, only the screen that approves, rejects and revokes them all.
    case 'support_access_grant':
      return { kind: 'supportAccess' };
    // `request` has no standalone route in this console yet (13 §5.4); everything else stays put.
    default:
      return { kind: 'none' };
  }
}

/** Navigates to whatever a notification is about, or to `/notifications` when there is nowhere else to go. */
export function useGoToNotification() {
  const navigate = useNavigate();
  return (notification: Notification) => {
    const target = notificationTarget(notification);
    switch (target.kind) {
      case 'ticket':
        return navigate({ to: '/tickets/$ticketId', params: { ticketId: target.ticketId } });
      case 'invoice':
        return navigate({
          to: '/billing/invoices/$invoiceId',
          params: { invoiceId: target.invoiceId },
        });
      case 'billingPeriod':
        return navigate({
          to: '/billing/periods/$periodId',
          params: { periodId: target.periodId },
        });
      case 'contract':
        return navigate({
          to: '/billing/contracts/$contractId',
          params: { contractId: target.contractId },
        });
      case 'knowledgeArticle':
        return navigate({
          to: '/knowledge/$articleId',
          params: { articleId: target.articleId },
        });
      case 'supportAccess':
        return navigate({ to: '/admin/support-access' });
      case 'none':
        return navigate({ to: '/notifications' });
    }
  };
}

/** Where a notice leads inside the portal: one of the portal's own pages, never the console's. */
export type PortalNotificationTarget =
  PortalTarget | { to: '/portal/requests/$requestId'; params: { requestId: string } };

/**
 * Where a notice leads in the portal, or `null` when the portal has no page of it. The notice keeps
 * the event's payload, whose `url` is the console's route for every recipient; the server chose the
 * requester's `requesterUrl` (`AM-232`) or the approver's `approverUrl` (`AM-238`) for the text it
 * rendered (`RecipientLinks`), and the portal makes the same choice here. A console route left over
 * leads to the portal's page of that same ticket or approval (`portalTarget`), and one the portal
 * has no page of (an invoice, a contract) leads nowhere: the portal never sends the person to the
 * console.
 */
export function portalNotificationTarget(
  notification: Pick<Notification, 'payload'>,
  userId: string | undefined,
  permissions: ReadonlySet<string>,
): PortalNotificationTarget | null {
  const payload =
    typeof notification.payload === 'object' && notification.payload !== null
      ? (notification.payload as Record<string, unknown>)
      : {};
  const me = userId?.toLowerCase();
  const requesterUrl = payload['requesterUrl'];
  const requester = payload['requesterUserId'];
  if (
    typeof requesterUrl === 'string' &&
    typeof requester === 'string' &&
    me !== undefined &&
    requester.toLowerCase() === me
  ) {
    return portalPage(requesterUrl);
  }

  const approverUrl = payload['approverUrl'];
  const approvers = payload['approverUserIds'];
  if (
    typeof approverUrl === 'string' &&
    Array.isArray(approvers) &&
    me !== undefined &&
    approvers.some((approver) => typeof approver === 'string' && approver.toLowerCase() === me)
  ) {
    return portalPage(approverUrl);
  }

  const url = payload['url'];
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) {
    return null;
  }
  if (url === '/portal' || url.startsWith('/portal/') || url.startsWith('/portal?')) {
    return portalPage(url);
  }
  const [pathname = '/', query = ''] = url.split('?', 2);
  const target = portalTarget(
    pathname,
    permissions,
    Object.fromEntries(new URLSearchParams(query)),
  );
  // `portalTarget` sends whatever it does not know to the portal's home: from a notice, no link.
  return target.to === '/portal' ? null : target;
}

/** One of the portal's own routes named by a link of the payload, or `null` for any other. */
function portalPage(url: string): PortalNotificationTarget | null {
  const path = (url.split('?', 1)[0] ?? '').replace(/\/+$/, '');
  const [, section, id, rest] =
    /^\/portal\/(tickets|requests|approvals)(?:\/([^/]+))?(\/.*)?$/.exec(path) ?? [];
  if (section === undefined || rest !== undefined) {
    return null;
  }
  switch (section) {
    case 'tickets':
      return id === undefined
        ? { to: '/portal/tickets' }
        : { to: '/portal/tickets/$ticketId', params: { ticketId: id } };
    case 'approvals':
      return id === undefined
        ? { to: '/portal/approvals' }
        : { to: '/portal/approvals/$approvalId', params: { approvalId: id } };
    default:
      return id === undefined
        ? null
        : { to: '/portal/requests/$requestId', params: { requestId: id } };
  }
}

/**
 * {@link portalNotificationTarget} for the person of the session: their user in the active tenant
 * and the permissions `portalTarget` weighs.
 */
export function usePortalNotificationTarget() {
  const session = useQuery(sessionQueryOptions());
  // A support session's `userId` is null (revisión del manual, ronda 2, punto 3); the portal is
  // unreachable from one anyway, since its permissions never route the session there.
  const userId = session.data?.activeTenant?.userId ?? undefined;
  const permissions = new Set(session.data?.permissions ?? []);
  return (notification: Pick<Notification, 'payload'>) =>
    portalNotificationTarget(notification, userId, permissions);
}
